/**
 * Bundle Builder - Sample Pack
 * Mobilia Stone
 *
 * Manages the selection of up to 4 products (each with a Finish variant)
 * into a Sample Pack product, added to cart via line item properties.
 *
 * Multi-variant update: multiple variants of the same product can be added,
 * each occupying their own slot. Modal stays open after adding to allow
 * additional variant selection.
 */

(function () {
  'use strict';

  /* ========== CONFIG ========== */
  const dataEl = document.getElementById('bundle-products-data');
  if (!dataEl) return;

  const CONFIG = JSON.parse(dataEl.textContent);
  const MAX_ITEMS = CONFIG.maxItems || 4;
  const SAMPLE_PACK_PRODUCT_ID = CONFIG.samplePackProductId;

  /* ========== STATE ========== */
  const STORAGE_KEY = 'mobilia_sample_pack';

  const state = {
    selections: loadSelections(),
  };

  function loadSelections() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length <= (CONFIG.maxItems || 4)) {
          return parsed;
        }
      }
    } catch (e) {
      // Silently fail
    }
    return [];
  }

  function saveSelections() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state.selections));
    } catch (e) {
      // Silently fail
    }
  }

  function clearSelections() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // Silently fail
    }
  }

  /* ========== DOM REFS ========== */
  const selectionBar = document.getElementById('BundleSelectionBar');
  const bundleCountEl = document.getElementById('BundleCount');
  const addToCartBtn = document.getElementById('BundleAddToCart');
  const modalOverlay = document.getElementById('BundleModalOverlay');
  const modal = document.getElementById('BundleModal');
  const modalClose = document.getElementById('BundleModalClose');
  const modalImage = document.getElementById('BundleModalImage');
  const modalTitle = document.getElementById('BundleModalTitle');
  const modalVariants = document.getElementById('BundleModalVariants');
  const modalAddBtn = document.getElementById('BundleModalAddBtn');
  const modalFeedback = document.getElementById('BundleModalFeedback');

  let currentModalProduct = null;
  let currentSelectedVariant = null;

  /* ========== HELPERS ========== */
  function getProductData(productId) {
    return CONFIG.products.find((p) => p.id === parseInt(productId));
  }

  /**
   * Check if a specific variant is already in the pack
   */
  function isVariantInPack(variantId) {
    return state.selections.some((s) => s.variantId === parseInt(variantId));
  }

  /**
   * Get all selections for a given product
   */
  function getSelectionsForProduct(productId) {
    return state.selections.filter((s) => s.productId === parseInt(productId));
  }

  /**
   * Check if a product has at least one variant in the pack
   */
  function isProductInPack(productId) {
    return getSelectionsForProduct(productId).length > 0;
  }

  function showToast(message) {
    let toast = document.querySelector('.bundle-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'bundle-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-visible');
    setTimeout(() => toast.classList.remove('is-visible'), 2500);
  }

  /* ========== RENDER ========== */
  function renderSlots() {
    const slots = selectionBar.querySelectorAll('.bundle-slot');

    slots.forEach((slot, index) => {
      const selection = state.selections[index];
      const emptyEl = slot.querySelector('.bundle-slot__empty');
      const filledEl = slot.querySelector('.bundle-slot__filled');

      if (selection) {
        slot.classList.add('is-filled');
        emptyEl.style.display = 'none';
        filledEl.style.display = 'flex';

        filledEl.querySelector('.bundle-slot__image').src = selection.image || '';
        filledEl.querySelector('.bundle-slot__image').alt = selection.productTitle;
        filledEl.querySelector('.bundle-slot__product-name').textContent = selection.productTitle;
        filledEl.querySelector('.bundle-slot__variant-name').textContent = selection.variantTitle;

        // Trigger pop animation
        slot.classList.remove('is-animating');
        void slot.offsetWidth; // Force reflow
        slot.classList.add('is-animating');
      } else {
        slot.classList.remove('is-filled', 'is-animating');
        emptyEl.style.display = 'flex';
        filledEl.style.display = 'none';
      }
    });

    // Update count
    bundleCountEl.textContent = state.selections.length;

    // Update add to cart button
    addToCartBtn.disabled = state.selections.length === 0;

    // Update product card badges and button states
    renderProductCards();
  }

  function renderProductCards() {
    const cards = document.querySelectorAll('.bundle-product-card:not(:has(.requestOnly))');
    cards.forEach((card) => {
      const productId = card.dataset.productId;
      const productSelections = getSelectionsForProduct(productId);
      const count = productSelections.length;
      const badge = card.querySelector('.bundle-product-card__selected-badge');
      const addBtn = card.querySelector('.bundle-product-card__add-btn');
      const packFull = state.selections.length >= MAX_ITEMS;

      if (count > 0) {
        // Product has at least one variant selected — show count badge
        badge.style.display = 'flex';
        badge.classList.add('is-visible');

        // Check if the product has more variants available to add
        const product = getProductData(productId);
        const totalVariants = product ? product.variants.filter(v => v.available).length : 0;
        const allVariantsAdded = count >= totalVariants;

        if (packFull || allVariantsAdded) {
          // Can't add more — disable button
          addBtn.disabled = true;
          addBtn.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg>
            ${count > 1 ? `${count} Added` : 'Added to Pack'}
          `;
        } else {
          // More variants can be added — keep button enabled with count indicator
          addBtn.disabled = false;
          addBtn.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            ${count} Added · Add More
          `;
        }
      } else {
        badge.style.display = 'none';
        badge.classList.remove('is-visible');
        addBtn.disabled = packFull;
        addBtn.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
          ${packFull ? 'Pack Full' : 'Add to Sample Pack'}
        `;
      }
    });
  }

  /* ========== MODAL ========== */
  function openModal(productId) {
    const product = getProductData(productId);
    if (!product) return;

    currentModalProduct = product;
    currentSelectedVariant = null;

    // Set content
    modalTitle.textContent = product.title;
    modalImage.src = product.featured_image || '';
    modalImage.alt = product.title;
    modalFeedback.textContent = '';
    modalFeedback.className = 'bundle-modal__feedback';
    modalAddBtn.disabled = true;

    renderModalVariants();

    // Show modal
    modalOverlay.style.display = 'flex';
    requestAnimationFrame(() => {
      modalOverlay.classList.add('is-visible');
    });

    // Trap focus
    document.body.style.overflow = 'hidden';
  }

  /**
   * Render (or re-render) the variant buttons inside the modal.
   * Called on open and after each successful add so states update in place.
   */
  function renderModalVariants() {
    if (!currentModalProduct) return;

    modalVariants.innerHTML = '';

    currentModalProduct.variants.forEach((variant) => {
      const alreadyAdded = isVariantInPack(variant.id);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bundle-variant-btn';

      if (!variant.available) btn.classList.add('is-unavailable');
      if (alreadyAdded) btn.classList.add('is-already-added');

      btn.dataset.variantId = variant.id;
      btn.disabled = !variant.available || alreadyAdded;

      const swatchImage = variant.featured_image || currentModalProduct.featured_image;

      btn.innerHTML = `
        <div class="bundle-variant-btn__swatch">
          ${swatchImage ? `<img src="${swatchImage}" alt="${variant.option1}">` : ''}
        </div>
        <span class="bundle-variant-btn__name">${variant.option1}</span>
        <span class="bundle-variant-btn__check">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"></polyline></svg>
        </span>
      `;

      // Only bind click for selectable variants
      if (variant.available && !alreadyAdded) {
        btn.addEventListener('click', () => selectVariant(variant, btn));
      }

      modalVariants.appendChild(btn);
    });

    // Reset selection state and add button after re-render
    currentSelectedVariant = null;
    modalAddBtn.disabled = true;

    // If pack is full, show feedback and disable add button
    if (state.selections.length >= MAX_ITEMS) {
      modalFeedback.textContent = 'Your Sample Pack is full. Remove an item to add a new one.';
      modalFeedback.className = 'bundle-modal__feedback';
    } else {
      modalFeedback.textContent = '';
      modalFeedback.className = 'bundle-modal__feedback';
    }
  }

  function closeModal() {
    modalOverlay.classList.remove('is-visible');
    setTimeout(() => {
      modalOverlay.style.display = 'none';
      document.body.style.overflow = '';
      currentModalProduct = null;
      currentSelectedVariant = null;
    }, 300);
  }

  function selectVariant(variant, btnEl) {
    currentSelectedVariant = variant;

    // Update UI — only highlight non-added buttons as selectable
    modalVariants.querySelectorAll('.bundle-variant-btn:not(.is-already-added)').forEach((b) => {
      b.classList.remove('is-selected');
    });
    btnEl.classList.add('is-selected');

    // Only enable add if pack isn't full
    modalAddBtn.disabled = state.selections.length >= MAX_ITEMS;

    // Update image if variant has one
    if (variant.featured_image) {
      modalImage.src = variant.featured_image;
    }

    modalFeedback.textContent = '';
  }

  function addFromModal() {
    if (!currentModalProduct || !currentSelectedVariant) return;

    if (state.selections.length >= MAX_ITEMS) {
      modalFeedback.textContent = 'Your Sample Pack is full. Remove an item to add a new one.';
      modalFeedback.className = 'bundle-modal__feedback';
      return;
    }

    if (isVariantInPack(currentSelectedVariant.id)) {
      modalFeedback.textContent = 'This variant is already in your Sample Pack.';
      modalFeedback.className = 'bundle-modal__feedback';
      return;
    }

    // Determine the best image (variant image > product image)
    const image = currentSelectedVariant.featured_image || currentModalProduct.featured_image;

    state.selections.push({
      productId: currentModalProduct.id,
      variantId: currentSelectedVariant.id,
      productTitle: currentModalProduct.title,
      variantTitle: currentSelectedVariant.option1,
      sku: currentSelectedVariant.sku || '',
      image: image,
    });

    saveSelections();
    renderSlots();

    const remaining = MAX_ITEMS - state.selections.length;
    const remainingMsg =
      remaining > 0
        ? `Added! ${remaining} sample${remaining === 1 ? '' : 's'} remaining.`
        : 'Sample Pack is full! Ready to add to cart.';

    // Check if all variants for this product have now been added
    const availableVariants = currentModalProduct.variants.filter(v => v.available);
    const allAdded = availableVariants.every(v => isVariantInPack(v.id));

    if (allAdded || remaining === 0) {
      // Nothing more to add from this product (or pack full) — close the modal
      showToast(remaining === 0 ? 'Sample Pack is full! Ready to add to cart.' : `${currentModalProduct.title} — all variants added.`);
      closeModal();
    } else {
      // Stay open — re-render variant buttons so the just-added one is now ticked/disabled
      renderModalVariants();
      modalFeedback.textContent = remainingMsg;
      modalFeedback.className = 'bundle-modal__feedback bundle-modal__feedback--success';
    }
  }

  /* ========== REMOVE FROM PACK ========== */
  function removeFromPack(slotIndex) {
    if (state.selections[slotIndex]) {
      const removed = state.selections.splice(slotIndex, 1)[0];
      saveSelections();
      renderSlots();
      showToast(`${removed.productTitle} — ${removed.variantTitle} removed from Sample Pack.`);
    }
  }

  /* ========== ADD TO CART ========== */
  async function addBundleToCart() {
    if (state.selections.length === 0) return;

    addToCartBtn.classList.add('is-loading');
    addToCartBtn.disabled = true;

    try {
      const productResponse = await fetch(`/products/sample-pack.js`);

      if (!productResponse.ok) {
        throw new Error('Could not fetch Sample Pack product');
      }

      const samplePackProduct = await productResponse.json();
      const samplePackVariantId = samplePackProduct.variants[0].id;

      const properties = {};
      state.selections.forEach((selection, index) => {
        properties[`Sample ${index + 1}`] = `${selection.productTitle} — ${selection.variantTitle}`;
        properties[`_sample_${index + 1}_sku`] = selection.sku;
        properties[`_sample_${index + 1}_image`] = selection.image;
      });
      properties['_bundle_product_ids'] = state.selections.map(s => s.variantId).join(',');

      const cartPayload = {
        items: [
          {
            id: samplePackVariantId,
            quantity: 1,
            properties: properties,
          },
        ],
      };

      const cartResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cartPayload),
      });

      if (!cartResponse.ok) {
        throw new Error('Failed to add to cart');
      }

      // Success!
      showSuccessOverlay();

      // Reset state
      state.selections = [];
      clearSelections();
      renderSlots();
    } catch (error) {
      console.error('Bundle add to cart error:', error);
      showToast('Something went wrong. Please try again.');
    } finally {
      addToCartBtn.classList.remove('is-loading');
      addToCartBtn.disabled = state.selections.length === 0;
    }
  }

  /* ========== SUCCESS OVERLAY ========== */
  function showSuccessOverlay() {
    const overlay = document.createElement('div');
    overlay.className = 'bundle-success-overlay';
    overlay.innerHTML = `
      <div class="bundle-success-card">
        <div class="bundle-success-card__icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"></polyline></svg>
        </div>
        <h3 class="bundle-success-card__title">Sample Pack Added!</h3>
        <p class="bundle-success-card__text">Your free sample pack has been added to your cart.</p>
        <div class="bundle-success-card__actions">
          <a href="/cart" class="bundle-success-card__btn bundle-success-card__btn--primary">View Cart</a>
          <button type="button" class="bundle-success-card__btn bundle-success-card__btn--secondary" id="BundleSuccessContinue">Continue Browsing</button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    requestAnimationFrame(() => overlay.classList.add('is-visible'));

    overlay.querySelector('#BundleSuccessContinue').addEventListener('click', () => {
      overlay.classList.remove('is-visible');
      setTimeout(() => overlay.remove(), 300);
    });

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        overlay.classList.remove('is-visible');
        setTimeout(() => overlay.remove(), 300);
      }
    });
  }

  /* ========== STICKY BAR SCROLL EFFECT ========== */
  function handleScroll() {
    if (window.scrollY > 100) {
      selectionBar.classList.add('is-scrolled');
    } else {
      selectionBar.classList.remove('is-scrolled');
    }
  }

  /* ========== BIND PRODUCT CARD BUTTONS (event delegation) ========== */
  function bindProductCardButtons() {
    const gridContainer = document.getElementById('Product-Grid-Container');
    if (!gridContainer || gridContainer.dataset.bundleBound) return;
    gridContainer.dataset.bundleBound = 'true';

    gridContainer.addEventListener('click', (e) => {
      const btn = e.target.closest('.bundle-product-card__add-btn');
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();
      const productId = btn.dataset.productId;

      if (state.selections.length >= MAX_ITEMS && !isProductInPack(productId)) {
        showToast('Your Sample Pack is full! Remove an item first.');
        return;
      }

      openModal(productId);
    });
  }

  /* ========== EVENT LISTENERS ========== */
  function init() {
    // Product card buttons (delegated)
    bindProductCardButtons();

    // Slot remove buttons
    selectionBar.querySelectorAll('.bundle-slot__remove').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const slot = btn.closest('.bundle-slot');
        const slotIndex = parseInt(slot.dataset.slot);
        removeFromPack(slotIndex);
      });
    });

    // Modal close
    modalClose.addEventListener('click', closeModal);
    modalOverlay.addEventListener('click', (e) => {
      if (e.target === modalOverlay) closeModal();
    });

    // Escape key closes modal
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modalOverlay.style.display !== 'none') {
        closeModal();
      }
    });

    // Modal add button
    modalAddBtn.addEventListener('click', addFromModal);

    // Add to cart
    addToCartBtn.addEventListener('click', addBundleToCart);

    // Scroll effect
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Re-render card states after Shopify AJAX updates (filtering/pagination)
    window.addEventListener('popstate', () => {
      setTimeout(renderProductCards, 500);
    });

    // Intercept fetch to detect when Shopify reloads the collection grid
    const originalFetch = window.fetch;
    window.fetch = function (...args) {
      return originalFetch.apply(this, args).then((response) => {
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
        if (url.includes('section_id') || url.includes('?sort_by') || url.includes('filter.')) {
          setTimeout(renderProductCards, 300);
        }
        return response;
      });
    };

    document.addEventListener('shopify:section:load', renderProductCards);

    // Initial render
    renderSlots();
  }

  // Initialise when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();